import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Modal from '../../components/Modal';
import ApiInspector from '../../components/ApiInspector';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { usePermesso } from '../../context/PermessiContext';
import { formatQuantity } from '../../utils/formatters';
import BopExcelTable, { BopExcelColumn } from '../../components/BopExcelTable';
import { API_DOCS } from './InventariFisiciPage.api';

const MODULO = 'magazzino.inventari_fisici';

type StatoInventario = 'BOZZA' | 'CONFERMATO';

interface DepositoOption {
  id: number;
  nome: string;
  tipo: string;
}

const DEP_TAG: Record<string, string> = { FISSO: 'FISSO', CONTO_TERZI: 'CT', COMMESSA: 'COM' };
function depositoLabel(d: { nome: string; tipo: string } | null | undefined): string {
  if (!d) return '';
  let nome = d.nome;
  if (d.tipo === 'COMMESSA' && nome.startsWith('Commessa: ')) nome = nome.slice('Commessa: '.length);
  if (nome.length > 32) nome = nome.slice(0, 31).trimEnd() + '…';
  const tag = DEP_TAG[d.tipo] || d.tipo;
  return `${nome} [${tag}]`;
}

interface Inventario {
  id: number;
  societaId: number;
  numero: number | null;
  anno: number | null;
  data: string;
  descrizione: string;
  stato: StatoInventario;
  deposito: { id: number; nome: string; tipo: string } | null;
  movimentoRettifica: { id: number; numero: number | null; anno: number | null; stato: string } | null;
  nRighe?: number;
}

interface FormState {
  depositoId: string;
  data: string;
  descrizione: string;
  note: string;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

const FORM_VUOTO: FormState = { depositoId: '', data: todayISO(), descrizione: '', note: '' };

export default function InventariFisiciPage() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const navigate = useNavigate();
  const { societaId, setSocietaId, listaSocieta, isUnica, pronta } = useFiltroSocieta();

  const [items, setItems] = useState<Inventario[]>([]);
  const [depositi, setDepositi] = useState<DepositoOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtroStato, setFiltroStato] = useState<'' | StatoInventario>('');
  const [filtroDeposito, setFiltroDeposito] = useState<string>('');
  const [ricerca, setRicerca] = useState('');

  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!pronta) return;
    if (!societaId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ societaId: String(societaId) });
      if (filtroStato)    qs.set('stato', filtroStato);
      if (filtroDeposito) qs.set('depositoId', filtroDeposito);
      // @api GET /api/inventari-fisici?societaId=&stato=&depositoId= -> { success: true, data: [{ id, numero, anno, data, descrizione, stato, deposito, movimentoRettifica, nRighe }] }
      const res = await apiFetch<{ success: boolean; data: Inventario[] }>(`/api/inventari-fisici?${qs.toString()}`);
      setItems(res.data ?? []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const loadDepositi = async () => {
    if (!societaId) { setDepositi([]); return; }
    try {
      // @api GET /api/depositi?societaId= -> { depositi: [{ id, nome, tipo }] }
      const res = await apiFetch<{ depositi: DepositoOption[] }>(`/api/depositi?societaId=${societaId}`);
      setDepositi(res.depositi ?? []);
    } catch {
      setDepositi([]);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [societaId, pronta, filtroStato, filtroDeposito]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDepositi(); }, [societaId]);

  const filtered = useMemo(() => {
    const q = ricerca.trim().toLowerCase();
    if (!q) return items;
    return items.filter(i =>
      i.descrizione.toLowerCase().includes(q)
      || (i.deposito?.nome || '').toLowerCase().includes(q)
    );
  }, [items, ricerca]);

  const apriNuovo = () => { setForm({ ...FORM_VUOTO }); setFormError(''); };
  const chiudi = () => { setForm(null); setFormError(''); };

  const salva = async () => {
    if (!form) return;
    if (!form.depositoId) { setFormError('Deposito obbligatorio'); return; }
    if (!form.data) { setFormError('Data obbligatoria'); return; }
    if (!form.descrizione.trim()) { setFormError('Descrizione obbligatoria'); return; }

    setSaving(true); setFormError('');
    try {
      // @api POST /api/inventari-fisici { societaId, depositoId, data, descrizione, note } -> { success: true, data: { id, righeCreate } }
      const res = await apiFetch<{ success: boolean; data: { id: number; righeCreate: number } }>('/api/inventari-fisici', {
        method: 'POST',
        body: JSON.stringify({
          societaId: Number(societaId),
          depositoId: Number(form.depositoId),
          data: form.data,
          descrizione: form.descrizione.trim(),
          note: form.note.trim() || null,
        }),
      });
      chiudi();
      navigate(`/magazzino/inventari-fisici/${res.data.id}`);
    } catch (e: any) {
      setFormError(e.message || 'Errore nell\'apertura inventario');
    } finally {
      setSaving(false);
    }
  };

  const noSocieta = !societaId;

  const depositiOpts = useMemo(
    () => depositi.map(d => ({ value: String(d.id), label: depositoLabel(d) })),
    [depositi],
  );

  const columns = useMemo<BopExcelColumn<Inventario>[]>(() => [
    {
      key: 'numero',
      header: 'Numero',
      accessor: (i) => i.numero != null ? `${i.numero}/${i.anno}` : `#${i.id}`,
      filter: { type: 'text' },
      width: '130px',
      render: (i) => i.numero != null
        ? <strong>{i.numero}/{i.anno}</strong>
        : <span className="bop-text-muted">Bozza #{i.id}</span>,
    },
    {
      key: 'data',
      header: 'Data',
      accessor: (i) => i.data || '',
      filter: { type: 'dateRange' },
      width: '110px',
      render: (i) => new Date(i.data).toLocaleDateString('it-IT'),
    },
    {
      key: 'deposito',
      header: 'Deposito',
      accessor: (i) => i.deposito?.id != null ? String(i.deposito.id) : '',
      filter: { type: 'enum', options: depositiOpts },
      render: (i) => depositoLabel(i.deposito),
    },
    {
      key: 'descrizione',
      header: 'Descrizione',
      accessor: (i) => i.descrizione,
      filter: { type: 'text' },
      render: (i) => i.descrizione,
    },
    {
      key: 'nRighe',
      header: 'Righe',
      accessor: (i) => i.nRighe ?? 0,
      filter: { type: 'number' },
      width: '90px',
      headerClassName: 'bop-text-center',
      cellClassName: 'bop-text-center',
      render: (i) => formatQuantity(i.nRighe ?? 0),
    },
    {
      key: 'movimentoRettifica',
      header: 'Movimento rettifica',
      accessor: (i) => i.movimentoRettifica
        ? `${i.movimentoRettifica.numero}/${i.movimentoRettifica.anno}`
        : '',
      filter: { type: 'text' },
      render: (i) => i.movimentoRettifica
        ? <span className="bop-text-muted">Mov. {i.movimentoRettifica.numero}/{i.movimentoRettifica.anno}</span>
        : <span className="bop-text-muted">—</span>,
    },
    {
      key: 'stato',
      header: 'Stato',
      accessor: (i) => i.stato,
      filter: {
        type: 'enum',
        options: [
          { value: 'BOZZA',      label: 'Bozza' },
          { value: 'CONFERMATO', label: 'Confermato' },
        ],
      },
      width: '120px',
      render: (i) => (
        <span className={`bop-badge ${i.stato === 'CONFERMATO' ? 'bop-badge--success' : 'bop-badge--neutral'}`}>
          {i.stato.charAt(0) + i.stato.slice(1).toLowerCase()}
        </span>
      ),
    },
  ], [depositiOpts]);

  return (
    <Layout>
      <div className="bop-page-header">
        <div>
          <h2 className="bop-page-title">Inventari fisici</h2>
          <p className="bop-text-muted">
            Apertura inventario → "congela" le quantità in giacenza al momento. La rilevazione si fa
            riga per riga sulla scheda. Alla conferma viene generato automaticamente un movimento
            di rettifica che allinea le giacenze alle quantità rilevate.
          </p>
        </div>
        {canEdit && (
          <button className="bop-btn-primary" onClick={apriNuovo} disabled={noSocieta || depositi.length === 0}>
            + Nuovo inventario
          </button>
        )}
      </div>

      <div className="bop-filter-bar">
        {!isUnica && (
          <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
            <option value="">Seleziona società…</option>
            {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}
        <input
          className="bop-input"
          placeholder="Cerca per descrizione o deposito…"
          value={ricerca}
          onChange={e => setRicerca(e.target.value)}
        />
        <select className="bop-select" value={filtroStato} onChange={e => setFiltroStato(e.target.value as any)}>
          <option value="">Tutti gli stati</option>
          <option value="BOZZA">Bozza</option>
          <option value="CONFERMATO">Confermato</option>
        </select>
        <select className="bop-select bop-filter-bar__fill" value={filtroDeposito} onChange={e => setFiltroDeposito(e.target.value)}>
          <option value="">Tutti i depositi</option>
          {depositi.map(d => <option key={d.id} value={d.id}>{depositoLabel(d)}</option>)}
        </select>
      </div>

      {error && <div className="bop-inline-error">{error}</div>}

      {noSocieta ? (
        <div className="bop-card">
          <div className="bop-empty-state">Seleziona una società per visualizzare gli inventari.</div>
        </div>
      ) : (
        <BopExcelTable<Inventario>
          data={filtered}
          loading={loading}
          rowKey={(i) => i.id}
          onRowClick={(i) => navigate(`/magazzino/inventari-fisici/${i.id}`)}
          initialSort={{ key: 'data', dir: 'desc' }}
          emptyState={
            <div className="bop-empty-state">
              Nessun inventario presente. {depositi.length === 0
                ? 'Crea prima almeno un deposito per poter aprire un inventario.'
                : 'Clicca "+ Nuovo inventario" per aprirne uno.'}
            </div>
          }
          columns={columns}
        />
      )}

      {form && (
        <Modal
          title="Apri nuovo inventario fisico"
          onClose={chiudi}
          footer={
            <>
              <button className="bop-btn-secondary" onClick={chiudi}>Annulla</button>
              <button className="bop-btn-primary" onClick={salva} disabled={saving}>
                {saving ? 'Apro…' : 'Apri inventario'}
              </button>
            </>
          }
        >
          {formError && <div className="bop-inline-error">{formError}</div>}

          <p className="bop-text-muted">
            Alla creazione le quantità in giacenza sul deposito vengono "congelate" come quantità
            teoriche. Sulla scheda dell'inventario potrai poi inserire le quantità rilevate fisicamente.
          </p>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Deposito *</label>
              <select
                className="bop-select"
                value={form.depositoId}
                onChange={e => setForm(f => f && { ...f, depositoId: e.target.value })}
                autoFocus
              >
                <option value="">— Seleziona deposito —</option>
                {depositi.map(d => <option key={d.id} value={d.id}>{depositoLabel(d)}</option>)}
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Data inventario *</label>
              <input
                type="date"
                className="bop-input"
                value={form.data}
                onChange={e => setForm(f => f && { ...f, data: e.target.value })}
              />
            </div>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Descrizione *</label>
            <input
              className="bop-input"
              value={form.descrizione}
              onChange={e => setForm(f => f && { ...f, descrizione: e.target.value })}
              placeholder="es. Inventario fine anno 2026 — Deposito SEDE"
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Note</label>
            <textarea
              className="bop-input"
              rows={2}
              value={form.note}
              onChange={e => setForm(f => f && { ...f, note: e.target.value })}
            />
          </div>
        </Modal>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
